// Motor de sincronización del modo sin conexión de ClinPed.
// - Reproduce la cola (outbox) contra el servidor al recuperar la conexión.
// - Mapea IDs temporales (local_*) a los reales que devuelve el backend.
// - Precarga datos para trabajar sin internet ("Preparar para trabajar sin conexión").
import axios from "axios";
import { v4 as uuid } from "uuid";
import {
  enqueue, listOutbox, deleteOutbox, patchOutbox, outboxCount,
  setRealId, getRealId, putCache, encBytes, decBytes,
} from "./store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instancia "cruda": pega a la red directamente, SIN la lógica offline (evita re-encolar).
const rawApi = axios.create({ baseURL: API });
rawApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const readKey = (url) => `GET ${url}`;

// ---------- Estado de sincronización ----------
const state = { syncing: false, pending: 0, error: null };
export function getSyncState() { return { ...state }; }
function setState(patch) {
  Object.assign(state, patch);
  window.dispatchEvent(new CustomEvent("clinped-offline-change"));
}

const isTemp = (id) => typeof id === "string" && id.startsWith("local_");

async function remapUrl(url) {
  const [pathPart, query] = url.split("?");
  const segs = pathPart.split("/");
  for (let i = 0; i < segs.length; i++) {
    if (isTemp(segs[i])) {
      const real = await getRealId(segs[i]);
      if (real) segs[i] = real;
    }
  }
  return segs.join("/") + (query ? `?${query}` : "");
}

async function remapBody(body) {
  if (!body || typeof body !== "object") return body;
  const out = { ...body };
  for (const k of ["patient_id", "consultation_id", "prescription_id"]) {
    if (isTemp(out[k])) {
      const real = await getRealId(out[k]);
      if (real) out[k] = real;
    }
  }
  return out;
}

async function replay(item) {
  const url = await remapUrl(item.url);
  const cfg = { method: item.method, url };
  if (item.kind === "multipart") {
    const fd = new FormData();
    const f = item.fields || {};
    if (f.category) fd.append("category", f.category);
    if (f.title) fd.append("title", f.title);
    if (item.file) {
      const buf = await decBytes(item.file);
      fd.append("file", new File([buf], item.file.name || "archivo", { type: item.file.type || "application/octet-stream" }));
    }
    cfg.data = fd;
  } else if (item.body != null) {
    cfg.data = await remapBody(item.body);
  }
  return rawApi.request(cfg);
}

export async function syncNow() {
  if (state.syncing || !navigator.onLine) return;
  const items = await listOutbox();
  if (!items.length) { setState({ pending: 0, error: null }); return; }
  setState({ syncing: true, error: null });
  for (const item of items) {
    if (!navigator.onLine) break;
    try {
      const res = await replay(item);
      if (item.method === "post" && item.tempId && res?.data) {
        const realId = res.data.patient_id || res.data.consultation_id || res.data.prescription_id || res.data.attachment_id;
        if (realId) await setRealId(item.tempId, realId);
      }
      await deleteOutbox(item.id);
    } catch (e) {
      if (!e.response) break; // seguimos sin conexión: reintentar más tarde
      await patchOutbox(item.id, { tries: (item.tries || 0) + 1, error: e.response?.data?.detail || `Error ${e.response?.status}` });
      setState({ error: "Un cambio no se pudo sincronizar. Revísalo en el panel." });
      break;
    }
  }
  const remaining = await outboxCount();
  setState({ syncing: false, pending: remaining });
  window.dispatchEvent(new CustomEvent("clinped-synced", { detail: { pending: remaining } }));
}

// ---------- Encolado de cambios hechos sin conexión (usado por el interceptor) ----------
function parseBody(config) {
  let d = config.data;
  if (typeof d === "string") { try { d = JSON.parse(d); } catch { /* ignore */ } }
  return d || {};
}

export async function handleOfflineMutation(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.url;
  const p = url.split("?")[0];
  const created_at = new Date().toISOString();

  // Adjuntos (multipart)
  if (method === "post" && /^\/patients\/[^/]+\/attachments$/.test(p) && config.data instanceof FormData) {
    const pid = p.split("/")[2];
    const file = config.data.get("file");
    const tempId = `local_att_${uuid()}`;
    let fileBlob = null;
    if (file && file.arrayBuffer) fileBlob = { ...(await encBytes(await file.arrayBuffer())), name: file.name, type: file.type };
    await enqueue({
      method, url, kind: "multipart", entity: "attachment", tempId, refPatientId: pid,
      fields: { title: config.data.get("title") || (file && file.name) || "Adjunto", category: config.data.get("category") || "otro", filename: (file && file.name) || "", is_note: false },
      file: fileBlob, title: config.data.get("title") || "Adjunto",
    });
    return { attachment_id: tempId, _offline: true };
  }

  const body = parseBody(config);

  if (method === "post" && p === "/patients") {
    const tempId = `local_pat_${uuid()}`;
    await enqueue({ method, url, entity: "patient", tempId, body, title: body.name });
    return { ...body, patient_id: tempId, record_number: "EXP-PENDIENTE", created_at, _offline: true };
  }
  if (method === "put" && /^\/patients\/[^/]+$/.test(p)) {
    const id = p.split("/")[2];
    await enqueue({ method, url, entity: "patient", body, refPatientId: id });
    return { ...body, patient_id: id, _offline: true };
  }
  if (method === "delete" && /^\/patients\/[^/]+$/.test(p)) {
    await enqueue({ method, url, entity: "patient" });
    return { ok: true, _offline: true };
  }
  if (method === "post" && p === "/consultations") {
    const tempId = `local_con_${uuid()}`;
    await enqueue({ method, url, entity: "consultation", tempId, refPatientId: body.patient_id, body });
    return { ...body, consultation_id: tempId, patient_id: body.patient_id, date: created_at, created_at, status: body.status || "en_progreso", _offline: true };
  }
  if (method === "put" && /^\/consultations\/[^/]+$/.test(p)) {
    const id = p.split("/")[2];
    await enqueue({ method, url, entity: "consultation", body });
    return { ...body, consultation_id: id, _offline: true };
  }
  if (method === "post" && p === "/prescriptions") {
    const tempId = `local_rx_${uuid()}`;
    await enqueue({ method, url, entity: "prescription", tempId, refPatientId: body.patient_id, body });
    return { ...body, prescription_id: tempId, created_at, _offline: true };
  }
  if (method === "post" && /^\/patients\/[^/]+\/lab-note$/.test(p)) {
    const pid = p.split("/")[2];
    const tempId = `local_att_${uuid()}`;
    await enqueue({
      method, url, kind: "json", entity: "attachment", tempId, refPatientId: pid, body,
      fields: { title: body.title || "Nota de laboratorio", category: body.category || "laboratorio", note: body.note || "", is_note: true, filename: "" },
      title: body.title || "Nota de laboratorio",
    });
    return { attachment_id: tempId, _offline: true };
  }
  if (method === "delete" && /^\/attachments\/[^/]+$/.test(p)) {
    await enqueue({ method, url, entity: "attachment" });
    return { ok: true, _offline: true };
  }
  if (method === "post" && p === "/audit/log") {
    await enqueue({ method, url, kind: "json", entity: "audit", body });
    return { ok: true, _offline: true };
  }
  return null; // no soportado offline
}

// ---------- Precarga para trabajar sin conexión ----------
export async function prefetchAll(onProgress) {
  const base = ["/auth/me", "/doctor/me", "/saved-drugs", "/compliance/summary"];
  let patients = [];
  try {
    const r = await rawApi.get("/patients");
    patients = r.data || [];
    await putCache(readKey("/patients"), patients);
  } catch { /* ignore */ }

  const perPatient = (id) => [
    `/patients/${id}`,
    `/patients/${id}/consultations`,
    `/patients/${id}/prescriptions`,
    `/patients/${id}/attachments`,
    `/patients/${id}/growth`,
  ];
  const urls = [...base, ...patients.flatMap((p) => perPatient(p.patient_id))];
  const total = urls.length;
  let done = 0;
  for (const url of urls) {
    try {
      const r = await rawApi.get(url);
      await putCache(readKey(url), r.data);
    } catch { /* ignore */ }
    done++;
    onProgress?.(done, total, patients.length);
  }
  return { patients: patients.length, total };
}

// ---------- Arranque ----------
let started = false;
export function startAutoSync() {
  if (started) return;
  started = true;
  outboxCount().then((n) => setState({ pending: n }));
  window.addEventListener("online", () => syncNow());
  setInterval(() => { if (state.pending > 0 && navigator.onLine) syncNow(); }, 20000);
  if (navigator.onLine) setTimeout(() => syncNow(), 1500);
}
